import { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { applyPalette, resolvePalette, saveAppearance } from '@/lib/appearance';
import { resetAppearance, setCustomColors, setPreset } from '@/store/appearance.slice';

/**
 * Subscribes to the appearance prefs, applies the resolved palette to the
 * document (CSS vars + native Telegram chrome) and persists prefs whenever they
 * change. Mount ONCE (in AppShell) — calling it elsewhere would just redo the
 * same idempotent apply.
 *
 * Initial prefs are hydrated synchronously in the slice's initialState
 * (`loadStoredAppearance`), and `main.tsx` does a one-shot apply before React
 * mounts, so there's no flash of the default theme.
 */
export function useAppearance() {
  const prefs = useAppSelector((s) => s.appearance);
  const dispatch = useAppDispatch();

  const palette = useMemo(() => resolvePalette(prefs), [prefs]);

  useEffect(() => {
    applyPalette(palette);
    saveAppearance(prefs);
  }, [palette, prefs]);

  return {
    prefs,
    presetId: prefs.presetId,
    setPreset: (id: string) => dispatch(setPreset(id)),
    setCustomColors: (colors: { bg?: string; accent?: string }) =>
      dispatch(setCustomColors(colors)),
    reset: () => dispatch(resetAppearance()),
  };
}
